/**
 * Assistant/template domain - built on apiClient via useRequest, not
 * useAppServices(). Replaces the AppService.assistant.* / AppService.template.*
 * lookups previously scattered across:
 *   - layouts/super-admin/AgentFramework is NOT here (that's agency.* -
 *     see queries/agency.js)
 *   - layouts/super-admin/Assistants/index.js (list + shared-list + plugins +
 *     knowledgebase + toggle/default/favourite mutations)
 *   - Assistants/Components/{CreateModal,CreateTemplate,DeleteModal,
 *     DeleteTemplate,EditModal,EditTemplate}/index.js (create/update/delete)
 *   - Assistants/Components/AddAWebsite/index.js (website-scrape polling)
 *   - Assistants/Components/ThirdPartyServices/MediaLibrary.js (media list)
 *
 * assistantKeys.all is the shared "assistants" prefix every mutation here
 * invalidates - matches the resource-name convention already established
 * across the rest of the app's TanStack Query migration. The two list key
 * builders intentionally keep the exact array shapes the old inline
 * useQuery calls used (no agencyId in the agency-scoped key) to avoid any
 * cache-identity change alongside the transport swap.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const assistantKeys = {
  all: ["assistants"],
  agencyList: (page, type, search) => ["assistants", "agency", page, type, search],
  sharedList: (page, search) => ["assistants", "super-admin-shared", page, search],
};

// AssistantTable ("Your Live Agents") - agency-scoped, paginated/searchable,
// optionally filtered by type. agencyId is a query filter only (it was never
// part of the old queryKey either, so this is preserved as-is).
export function useAssistantsList({ agencyId, page, type, search }, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: assistantKeys.agencyList(page, type, search),
    queryFn: async () => {
      const { response, error } = await request(GET, "/assistant", {
        query: `agency_id=${agencyId}&search=${search === undefined ? "" : search}&page=${page}&limit=10${type !== "" ? "&type=" + type : ""}&pin=true`,
      });
      if (error) throw error;
      return response;
    },
    ...options,
  });
}

// SuperadminAssistantTable ("Maybe* Shared Agents").
export function useSharedAssistantsList({ page, search }, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: assistantKeys.sharedList(page, search),
    queryFn: async () => {
      const { response, error } = await request(GET, "/assistant", {
        query: `agency_id=superYxadminagency_id&type=super-admin&distributed=true&page=${page}&limit=10&search=${search === undefined ? "" : search}`,
      });
      if (error) throw error;
      return response;
    },
    ...options,
  });
}

export function useCreateAssistant() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/assistant", { payload, toaster: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assistantKeys.all }),
  });
}

// Shared by every toggle/save site (AssistantTable + SuperadminAssistantTable
// status toggles, superAdminAgents save, EditModal). Callers differ only on
// whether they want a success toast, so `toaster` travels with the call
// instead of being baked in - EditModal passes true, the toggle call sites
// pass none (falsy), exactly like the old per-call-site AppService options.
export function useUpdateAssistant() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, toaster }) => request(PUT, "/assistant", { payload, toaster }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assistantKeys.all }),
  });
}

// Shared by DeleteModal and DeleteTemplate - see queries/assistants.js
// consumers' notes in the migration report: DeleteTemplate calling this
// (instead of a template-delete endpoint) is a pre-existing oddity, not
// something introduced by this migration.
export function useDeleteAssistant() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (query) => request(DELETE, "/assistant", { query, toaster: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assistantKeys.all }),
  });
}

export function useAssistantPlugins(options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["plugins"],
    queryFn: async () => {
      const { response, error } = await request(GET, "/assistant/getplugins");
      if (error) throw error;
      return response;
    },
    ...options,
  });
}

export function useKnowledgebase(agencyId, page, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["knowledge-base", page],
    queryFn: async () => {
      const { response, error } = await request(GET, "/assistant/knowledgebase", {
        query: `agency_id=${agencyId}&page=${page}&limit=10&search=`,
        toaster: true,
      });
      if (error) throw error;
      return response;
    },
    ...options,
  });
}

export function useFavouriteAssistant() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/assistant/add-favourite", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assistantKeys.all }),
  });
}

export function useUpdateAssistantAccountDefault() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/assistant/updateaccount", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assistantKeys.all }),
  });
}

export function useUpdateDefaultAssistant() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/assistant/update_default_assistant", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assistantKeys.all }),
  });
}

// AddAWebsite's scrape flow: start a crawl job, then poll status, then list
// the scraped URLs. All three were plain imperative calls (not
// useQuery/useMutation) in the old code - a setInterval poll doesn't fit
// TanStack's declarative refetch model - so this stays a bag of thin
// request()-bound functions rather than forcing it into query/mutation shape.
export function useAssistantWebsiteScraper() {
  const request = useRequest();
  return {
    fetchWebsiteUrl: (payload) => request(POST, "/assistant/fetchurl", { payload }),
    getScrapeStatus: (jobId) => request(GET, "/assistant/scrape/status", { query: `jobId=${jobId}` }),
    getScrapedUrls: (profileId, jobId) =>
      request(GET, "/assistant/scrape/urls", { query: `profileId=${profileId}&jobId=${jobId}` }),
  };
}

// MediaLibrary.js's file list + client-side blob conversion (so the picked
// files can be handed to the same upload path as a drag-and-drop). The blob
// conversion is UI-consumption logic, not really an API concern, but it was
// already folded into this same cached queryFn pre-migration, so it's kept
// exactly as-is rather than splitting it out as part of a transport-only swap.
export function useAssistantMediaLibrary(locationId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["media-library", locationId],
    queryFn: async () => {
      const { response, error } = await request(GET, "/assistant/files/media-library", {
        query: `altId=${locationId}&altType=location`,
      });
      if (error) throw error;
      const files = response.data.files;
      const convertedFiles = await Promise.all(
        files.map(async (file) => {
          try {
            const fileResponse = await fetch(file.url);
            const blob = await fileResponse.blob();
            return new File([blob], file.name, { type: file.contentType });
          } catch (e) {
            return null;
          }
        }),
      ).then((results) => results.filter(Boolean));
      return { mediaLibraryData: files, convertedFiles };
    },
    ...options,
  });
}

// GET /modalcreate - shared cache key ("model-options") with
// layouts/super-admin/UiPrompts/promptlabels/ModelPrompts/Create.js, which is
// out of this migration's scope and still calls it via useAppServices(). Key
// shape is preserved exactly so both sides keep hitting the same cache entry.
export function useModelOptions(options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["model-options"],
    queryFn: async () => {
      const { response, error } = await request(GET, "/modalcreate");
      if (error) throw error;
      return response?.data;
    },
    ...options,
  });
}

export function useCreateAssistantTemplate() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/assistant_template", { payload, toaster: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["assistant-templates"] }),
  });
}

export function useUpdateAssistantTemplate() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/assistant_template", { payload, toaster: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["assistant-templates"] }),
  });
}
